import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card } from "react-bootstrap";
import { Link } from "react-router-dom";
import { fetchUserProfile, updateReferralPreferences } from "../apiService";
import LoginModal from "../Components/LoginModal"; // adjust path to match your folder structure
import "../App.css";

// Two supported payout methods, taken directly from the reward_type field
// on PATCH /referrals/account/referral-preferences (a plain string, no
// documented enum) — mapped 1:1 to the two options as specified:
//   "credit" -> Credit (requires bank details)
//   "cash"   -> Cash Payout (uses the mobile number already on the
//                customer's profile — nothing to type in here)
type RewardType = "cash" | "credit";

const ReferralPreferences = () => {
  const [rewardType, setRewardType] = useState<RewardType>("credit");

  // Cash payout uses the mobile number already on file for this account —
  // read-only here, same pattern as the referrer details on Referral.tsx.
  const [profileMobile, setProfileMobile] = useState("");

  // Full bank details, only required/shown when rewardType === "credit".
  const [bankDetails, setBankDetails] = useState({
    accountHolderName: "",
    accountNumber: "",
    bankName: "",
    branchCode: "",
  });

  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState<{
    rewardType?: string;
    accountNumberLast4?: string;
  } | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [pageLoading, setPageLoading] = useState(true);

  // Confirmation modal — same pattern as ManagePackage.tsx's "Confirm
  // Package Change" modal: validate first, then require an explicit
  // checkbox confirmation before the change actually goes to the backend.
  const [showModal, setShowModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  // Whether to show the login modal as an overlay on top of this page.
  // Purely local state — never redirects or changes the URL. The page
  // stays rendered behind it the whole time.
  const [showLoginModal, setShowLoginModal] = useState(false);

  useEffect(() => {
    let accessToken: string | null = null;
    let portalEndCustomerId: string | null = null;

    try {
      accessToken = localStorage.getItem("accessToken");
      portalEndCustomerId = localStorage.getItem("portalEndCustomerId");
    } catch (err) {
      console.error(
        "ReferralPreferences: localStorage access blocked (private mode / ITP):",
        err
      );
    }

    if (!accessToken || !portalEndCustomerId) {
      setPageLoading(false);
      setShowLoginModal(true);
      return;
    }

    setShowLoginModal(false);

    // TODO: confirm with Max — there's currently no GET endpoint for
    // referral preferences, only PATCH. Until one exists, we can't
    // pre-populate this form with whatever the customer last saved; we
    // only pre-fill their name (default account holder name for Credit)
    // and mobile number (required for Cash Payout) from the profile
    // endpoint.
    const loadDefaults = async () => {
      try {
        setPageLoading(true);
        const profile = await fetchUserProfile(portalEndCustomerId);
        const fullName = [profile?.firstName, profile?.lastName]
          .filter(Boolean)
          .join(" ");
        setBankDetails((prev) => ({
          ...prev,
          accountHolderName: fullName || prev.accountHolderName,
        }));
        setProfileMobile(profile?.mobileNumber || "");
      } catch (error) {
        console.error("Error loading profile defaults:", error);
      } finally {
        setPageLoading(false);
      }
    };

    loadDefaults();
  }, []);

  const handleBankChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setBankDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg(null);
    setSaved(null);

    if (rewardType === "credit") {
      if (
        !bankDetails.accountHolderName.trim() ||
        !bankDetails.accountNumber.trim() ||
        !bankDetails.bankName.trim() ||
        !bankDetails.branchCode.trim()
      ) {
        setErrorMsg("Please fill in all bank detail fields.");
        return;
      }
    } else {
      // "cash" relies on the mobile number already on the customer's
      // profile — if that hasn't loaded, don't let them proceed silently.
      if (!profileMobile) {
        setErrorMsg(
          "We couldn't find a mobile number on your profile. Please update your profile first."
        );
        return;
      }
    }

    // Fields are valid — show the confirmation modal instead of saving
    // immediately. The actual API call happens in handleConfirmSave.
    setShowModal(true);
  };

  const handleConfirmSave = async () => {
    setShowModal(false);
    setErrorMsg(null);

    try {
      setLoading(true);

      // Credit needs full bank_details. Cash has no dedicated field on the
      // backend for "the mobile number to pay out to" yet, so — following
      // the same stopgap convention already used elsewhere for this
      // endpoint — it rides in bank_details.account_number, with the
      // holder name pulled from the profile.
      const payloadBankDetails =
        rewardType === "credit"
          ? {
              accountHolderName: bankDetails.accountHolderName.trim(),
              accountNumber: bankDetails.accountNumber.trim(),
              bankName: bankDetails.bankName.trim(),
              branchCode: bankDetails.branchCode.trim(),
              iban: "",
            }
          : {
              accountHolderName: bankDetails.accountHolderName.trim(),
              accountNumber: profileMobile,
              bankName: "",
              branchCode: "",
              iban: "",
            };

      const result = await updateReferralPreferences(
        rewardType,
        payloadBankDetails
      );

      if (!result.success) {
        throw new Error(result.message || "Failed to save preferences.");
      }

      setSaved({
        rewardType: result.rewardType,
        accountNumberLast4: result.accountNumberLast4,
      });
      setTermsAccepted(false);
    } catch (error) {
      setErrorMsg(
        error instanceof Error ? error.message : "Failed to save preferences."
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancelModal = () => {
    setShowModal(false);
  };

  return (
    <div className="referral-page">
      {/* LOGIN OVERLAY — shown on top of the page when the visitor isn't
          signed in. The page stays rendered behind it; this never
          redirects or changes the URL. */}
      {showLoginModal && (
        <LoginModal handleClose={() => setShowLoginModal(false)} />
      )}

      <section className="referral-form-section">
        <Container>
          <Row className="justify-content-center">
            <Col lg={7}>
              <Card className="rf-card">
                <Card.Body>
                  <div className="rp-header">
                    <h2 className="form-title">Payout Preferences</h2>
                    <Link to="/referral" className="rp-back-link">
                      <i className="bi bi-arrow-left"></i> Back to referrals
                    </Link>
                  </div>
                  <p className="rp-subtitle">
                    Choose how you'd like to receive your R100 reward once a
                    friend you refer is activated.
                  </p>

                  {errorMsg && (
                    <div className="alert alert-danger">{errorMsg}</div>
                  )}

                  {saved && (
                    <div className="rp-success-note">
                      <i className="bi bi-check-circle-fill"></i>
                      <span>
                        Saved — reward payouts will now go via{" "}
                        <b>
                          {saved.rewardType === "credit"
                            ? "Credit"
                            : "Cash Payout"}
                        </b>
                        {saved.accountNumberLast4 &&
                          ` (ending ${saved.accountNumberLast4})`}
                        .
                      </span>
                    </div>
                  )}

                  <form onSubmit={handleSubmit}>
                    <div className="rf-section">
                      <div className="rf-section-header">
                        <span className="rf-label-plain">PAYOUT METHOD</span>
                      </div>

                      <div className="rp-method-toggle">
                        <button
                          type="button"
                          className={`rp-method-btn ${
                            rewardType === "credit" ? "rp-method-active" : ""
                          }`}
                          onClick={() => setRewardType("credit")}
                        >
                          <i className="bi bi-bank"></i>
                          <span>
                            Credit
                            <small>Paid into your bank account</small>
                          </span>
                        </button>

                        <button
                          type="button"
                          className={`rp-method-btn ${
                            rewardType === "cash" ? "rp-method-active" : ""
                          }`}
                          onClick={() => setRewardType("cash")}
                        >
                          <i className="bi bi-phone"></i>
                          <span>
                            Cash Payout
                            <small>Sent to your profile mobile number</small>
                          </span>
                        </button>
                      </div>
                    </div>

                    {rewardType === "cash" ? (
                      <div className="rf-section">
                        <div className="rf-field">
                          <label className="rf-field-label">
                            MOBILE NUMBER ON FILE
                          </label>
                          <div className="rf-input-wrap">
                            <i className="bi bi-phone rf-input-icon"></i>
                            <input
                              className="rf-input rf-input-readonly"
                              type="tel"
                              value={
                                pageLoading
                                  ? "Loading..."
                                  : profileMobile || "No mobile number on file"
                              }
                              readOnly
                            />
                          </div>
                        </div>
                        <div className="rf-credit-note" style={{ marginTop: "1rem" }}>
                          {/* <i className="bi bi-info-circle"></i> */}
                          <span>
                            Cash payouts goes to the mobile number on your
                            FTTHSA profile.{" "}
                            {/* <Link to="/profile">Update it there</Link> if
                            it's changed. */}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="rf-section">
                        <div className="rf-grid rf-grid-2">
                          <div className="rf-field rf-field-full">
                            <label className="rf-field-label">
                              ACCOUNT HOLDER NAME
                            </label>
                            <div className="rf-input-wrap">
                              <i className="bi bi-person rf-input-icon"></i>
                              <input
                                className="rf-input"
                                type="text"
                                name="accountHolderName"
                                value={bankDetails.accountHolderName}
                                onChange={handleBankChange}
                                placeholder={
                                  pageLoading ? "Loading..." : "Full name"
                                }
                                required
                              />
                            </div>
                          </div>

                          <div className="rf-field">
                            <label className="rf-field-label">
                              BANK NAME
                            </label>
                            <div className="rf-input-wrap">
                              <i className="bi bi-bank rf-input-icon"></i>
                              <input
                                className="rf-input"
                                type="text"
                                name="bankName"
                                value={bankDetails.bankName}
                                onChange={handleBankChange}
                                placeholder="e.g. FNB"
                                required
                              />
                            </div>
                          </div>

                          <div className="rf-field">
                            <label className="rf-field-label">
                              BRANCH CODE
                            </label>
                            <div className="rf-input-wrap">
                              <i className="bi bi-hash rf-input-icon"></i>
                              <input
                                className="rf-input"
                                type="text"
                                name="branchCode"
                                value={bankDetails.branchCode}
                                onChange={handleBankChange}
                                placeholder="e.g. 250655"
                                required
                              />
                            </div>
                          </div>

                          <div className="rf-field rf-field-full">
                            <label className="rf-field-label">
                              ACCOUNT NUMBER
                            </label>
                            <div className="rf-input-wrap">
                              <i className="bi bi-credit-card rf-input-icon"></i>
                              <input
                                className="rf-input"
                                type="text"
                                name="accountNumber"
                                value={bankDetails.accountNumber}
                                onChange={handleBankChange}
                                placeholder="Account number"
                                required
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="rf-footer">
                      <button
                        type="submit"
                        className="rf-submit-btn"
                        disabled={loading || pageLoading}
                      >
                        REVIEW & SAVE
                        <i className="bi bi-arrow-right rf-submit-icon"></i>
                      </button>
                    </div>
                  </form>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CONFIRMATION MODAL — same pattern as ManagePackage.tsx's
          "Confirm Package Change" modal: summary + required checkbox
          before the change is actually submitted. */}
      {showModal && (
        <div
          className="modal show"
          tabIndex={-1}
          role="dialog"
          style={{ display: "block" }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div
                className="modal-header"
                style={{ backgroundColor: "#e2520f", color: "white" }}
              >
                <h5 className="modal-title">Confirm Payout Preference</h5>
              </div>

              <div className="modal-body">
                <p>
                  {rewardType === "credit" ? (
                    <>
                      Your R100 referral reward will be paid via{" "}
                      <strong>Credit</strong> to:
                      <br />
                      <strong>{bankDetails.accountHolderName}</strong>
                      <br />
                      {bankDetails.bankName} · Account ending{" "}
                      {bankDetails.accountNumber.slice(-4)} · Branch code{" "}
                      {bankDetails.branchCode}
                    </>
                  ) : (
                    <>
                      Your R100 referral reward will be paid via{" "}
                      <strong>Cash Payout</strong> to the mobile number on
                      your profile:
                      <br />
                      <strong>{profileMobile}</strong>
                    </>
                  )}
                </p>

                <div className="mt-3">
                  <h6 className="fw-bold">Please note:</h6>
                  <ul className="text-muted small mb-3">
                    <li>This becomes your default payout method going forward.</li>
                    <li>
                      It only applies to future referrals that get activated
                      — it doesn't change how past rewards were paid.
                    </li>
                    <li>You can come back and change this at any time.</li>
                  </ul>

                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="payoutTermsCheck"
                      checked={termsAccepted}
                      onChange={() => setTermsAccepted(!termsAccepted)}
                    />
                    <label
                      className="form-check-label"
                      htmlFor="payoutTermsCheck"
                    >
                      I confirm these payout details are correct.
                    </label>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <div className="d-flex justify-content-between w-100">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={handleCancelModal}
                    style={{ backgroundColor: "#ec4d13", borderColor: "#ec4d13" }}
                  >
                    No, Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleConfirmSave}
                    disabled={!termsAccepted || loading}
                    style={{ backgroundColor: "#ec4d13", borderColor: "#ec4d13" }}
                  >
                    {loading ? "Saving..." : "Yes, Confirm"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .rf-card {
          border-radius: 20px;
          border: none;
          box-shadow: 0 8px 30px rgba(0,0,0,0.08);
        }
        .rf-card .card-body { padding: 2rem; }
        .form-title { font-weight: 700; margin: 0; }
        .rp-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 0.75rem;
          margin-bottom: 0.5rem;
        }
        .rp-back-link {
          font-size: 0.85rem;
          font-weight: 600;
          color: #e65c1c;
          text-decoration: none;
        }
        .rp-subtitle {
          color: #6b6b6b;
          font-size: 0.9rem;
          margin-bottom: 1.5rem;
        }
        .rp-success-note {
          display: flex;
          align-items: flex-start;
          gap: 0.6rem;
          background: #eef8ee;
          border: 1px solid #cfe9cf;
          border-radius: 10px;
          padding: 0.85rem 1rem;
          font-size: 0.88rem;
          color: #2f6b2f;
          margin-bottom: 1.5rem;
        }
        .rp-success-note i { color: #2f9e44; margin-top: 0.1rem; }
        .rf-section { margin-bottom: 1.75rem; }
        .rf-section-header {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          margin-bottom: 1.1rem;
        }
        .rf-label-plain {
          font-weight: 700;
          font-size: 0.9rem;
          letter-spacing: 0.02em;
          color: #1f1f1f;
        }
        .rp-method-toggle {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
        }
        @media (max-width: 576px) {
          .rp-method-toggle { grid-template-columns: 1fr; }
        }
        .rp-method-btn {
          display: flex;
          align-items: center;
          gap: 0.9rem;
          text-align: left;
          background: #fff;
          border: 1.5px solid #e2e2e2;
          border-radius: 14px;
          padding: 1rem;
          font-size: 1.3rem;
          color: #9a9a9a;
          cursor: pointer;
          transition: border-color 0.15s ease, background 0.15s ease;
        }
        .rp-method-btn span {
          display: flex;
          flex-direction: column;
          font-size: 0.95rem;
          font-weight: 700;
          color: #1f1f1f;
        }
        .rp-method-btn span small {
          font-weight: 400;
          font-size: 0.75rem;
          color: #7a7a7a;
          margin-top: 0.15rem;
        }
        .rp-method-active {
          border-color: #e65c1c;
          background: #fff6f1;
        }
        .rp-method-active i { color: #e65c1c; }
        .rf-grid { display: grid; gap: 1rem; }
        .rf-grid-2 { grid-template-columns: 1fr 1fr; }
        @media (max-width: 576px) {
          .rf-grid-2 { grid-template-columns: 1fr; }
        }
        .rf-field-full { grid-column: 1 / -1; }
        .rf-field-label {
          display: block;
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.04em;
          color: #6b6b6b;
          margin-bottom: 0.4rem;
        }
        .rf-input-wrap { position: relative; }
        .rf-input-icon {
          position: absolute;
          left: 14px;
          top: 50%;
          transform: translateY(-50%);
          color: #9a9a9a;
          font-size: 1rem;
          pointer-events: none;
        }
        .rf-input {
          width: 100%;
          padding: 0.7rem 0.9rem 0.7rem 2.6rem;
          border: 1px solid #e2e2e2;
          border-radius: 10px;
          font-size: 0.92rem;
          outline: none;
          background: #fff;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }
        .rf-input:focus {
          border-color: #e65c1c;
          box-shadow: 0 0 0 3px rgba(230, 92, 28, 0.12);
        }
        .rf-footer { margin-top: 0.5rem; }
        .rf-submit-btn {
          background: #e65c1c;
          color: #fff;
          border: none;
          border-radius: 12px;
          padding: 0.9rem 1.8rem;
          font-weight: 700;
          letter-spacing: 0.03em;
          display: inline-flex;
          align-items: center;
          gap: 0.6rem;
        }
        .rf-submit-btn:disabled { opacity: 0.7; cursor: not-allowed; }
        .rf-submit-icon {
          background: rgba(255,255,255,0.25);
          border-radius: 50%;
          width: 22px;
          height: 22px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-size: 0.75rem;
        }
      `}</style>
    </div>
  );
};

export default ReferralPreferences;